import { GraduationCap, ShieldCheck, Heart, Sprout } from "lucide-react";

/**
 * Education-first "why choose us" section — positions ChildCheck as built by/for
 * early-childhood educators, not a generic ops tool. Appears between the StatBar
 * and the feature grid to anchor the narrative.
 */
export const EducationPromise = () => {
  const pillars = [
    {
      Icon: GraduationCap,
      title: "Built with Ohio educators",
      copy: "Every form, ratio alert, and lesson-plan tool was shaped alongside licensed directors in Columbus, Cleveland, and Cincinnati. We speak your daily reality — naps, DCY 01234, the surprise licensing visit.",
    },
    {
      Icon: ShieldCheck,
      title: "Inspector-ready, always",
      copy: "Ratios, staff training expiries, playground inspections, and attendance PDFs stay audit-current on their own. When an inspector walks in, you already have the paperwork.",
    },
    {
      Icon: Heart,
      title: "More minutes with kids",
      copy: "Automating enrollment, billing, and parent updates gives lead teachers back 6–9 hours a week — more lap-time reading, less spreadsheet evenings.",
    },
    {
      Icon: Sprout,
      title: "Grows with your program",
      copy: "Whether you're a one-room home or a 4-location academy, the same workspace scales. Add classrooms, staff, and centers without relearning the tool.",
    },
  ];

  return (
    <section
      id="education-promise"
      data-testid="education-promise-section"
      className="relative py-24 bg-stone-50 overflow-hidden"
    >
      <div
        className="absolute inset-0 pointer-events-none opacity-40"
        style={{
          backgroundImage:
            "radial-gradient(circle at 15% 10%, rgba(6,95,70,0.06) 0 40%, transparent 60%), radial-gradient(circle at 85% 90%, rgba(251,146,60,0.05) 0 35%, transparent 60%)",
        }}
      />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-[0.25em] font-semibold text-emerald-700">
            Why Ohio directors choose ChildCheck
          </div>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl font-medium text-stone-900 tracking-tight">
            Early-childhood ops, taught by the classroom.
          </h2>
          <p className="mt-5 text-lg text-stone-600 max-w-2xl leading-relaxed">
            We don't think of child care as "a business vertical." We build for small, licensed programs
            who know every child's middle name — and still need to pass a DCY inspection on a Tuesday afternoon.
          </p>
        </div>

        <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {pillars.map((p, i) => (
            <article
              key={p.title}
              data-testid={`promise-card-${i}`}
              className="group relative rounded-3xl bg-white border border-stone-200 p-7 hover:border-emerald-200 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
            >
              <div className="w-11 h-11 rounded-2xl bg-emerald-50 border border-emerald-100 grid place-items-center text-emerald-700 group-hover:bg-emerald-700 group-hover:text-white transition-colors">
                <p.Icon className="w-5 h-5" />
              </div>
              <h3 className="mt-5 font-display text-xl font-medium text-stone-900 tracking-tight">{p.title}</h3>
              <p className="mt-3 text-sm text-stone-600 leading-relaxed">{p.copy}</p>
              <div className="mt-5 text-[10px] uppercase tracking-widest font-bold text-stone-400 group-hover:text-emerald-700 transition-colors">
                0{i + 1} · Core promise
              </div>
            </article>
          ))}
        </div>

        {/* Quotation strip */}
        <figure
          data-testid="education-pull-quote"
          className="mt-20 relative rounded-3xl bg-gradient-to-br from-emerald-950 to-emerald-900 text-white p-10 lg:p-14 overflow-hidden"
        >
          <div
            className="absolute inset-0 opacity-20 pointer-events-none"
            style={{
              backgroundImage:
                "radial-gradient(circle at 80% 10%, rgba(251,146,60,0.4) 0 30%, transparent 70%)",
            }}
          />
          <blockquote className="relative max-w-3xl font-display text-2xl lg:text-3xl leading-snug font-light text-emerald-50">
            “Our staff stopped staying past 6 to finish paperwork. The DCY 01234
            signed itself, the attendance printed itself, and our last DCY inspection took a fraction of the usual time.”
          </blockquote>
          <figcaption className="relative mt-6 text-sm text-emerald-200/70">
            — <span className="italic">An anonymous director in central Ohio · licensed for 52</span>
          </figcaption>
        </figure>
      </div>
    </section>
  );
};

export default EducationPromise;
